import { useState } from "react";
import Marquee from "react-fast-marquee";
import { motion } from "framer-motion";
import { X, Tag, ArrowUpRight } from "lucide-react";
import { Reveal, Label } from "./Reveal";

// Slim dismissible announcement bar, fixed at the very top.
export const OffersBar = ({ offers, show, onClose }) => {
  if (!show || !offers.length) return null;
  return (
    <div className="fixed top-0 inset-x-0 z-[60] h-10 bg-red-600 text-white flex items-center" data-testid="offers-bar">
      <div className="flex-1 overflow-hidden">
        <Marquee speed={45} gradient={false} autoFill>
          {offers.map((o, i) => (
            <span key={o.offer_id + i} className="mx-8 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide">
              <Tag size={14} /> {o.title}{o.subtitle ? ` — ${o.subtitle}` : ""}
              <span className="text-black/40 ml-8">✦</span>
            </span>
          ))}
        </Marquee>
      </div>
      <button onClick={onClose} className="px-3 h-full hover:bg-black/20 transition-colors shrink-0" aria-label="Dismiss offers" data-testid="offers-bar-close">
        <X size={16} />
      </button>
    </div>
  );
};

// Full promo section with optional uploaded banner images.
export const OffersBanner = ({ offers }) => {
  if (!offers.length) return null;
  const withImg = offers.filter((o) => o.image);
  const textOnly = offers.filter((o) => !o.image);

  return (
    <section id="offers" className="relative py-20 md:py-28 bg-[#0d0d0d] border-b border-white/10">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <Reveal>
          <Label>Live Offers</Label>
          <h2 className="mt-4 font-heading text-4xl sm:text-6xl font-bold uppercase tracking-tighter text-white">
            This month's <span className="text-red-600">deals</span>
          </h2>
        </Reveal>

        {withImg.length > 0 && (
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
            {withImg.map((o, i) => (
              <Reveal key={o.offer_id} delay={i * 0.08}>
                <div className="group relative overflow-hidden border border-white/10 bg-black">
                  <img src={o.image} alt={o.title} className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <h3 className="font-heading text-2xl font-bold uppercase text-white">{o.title}</h3>
                    {o.subtitle && <p className="text-zinc-300 mt-1 text-sm">{o.subtitle}</p>}
                    {o.link && (
                      <a href={o.link} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold uppercase text-red-500">
                        Know more <ArrowUpRight size={15} />
                      </a>
                    )}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        )}

        {textOnly.length > 0 && (
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {textOnly.map((o, i) => (
              <Reveal key={o.offer_id} delay={i * 0.06}>
                <div className="border border-white/10 bg-[#111] p-6 hover:border-red-600/50 transition-colors h-full">
                  <Tag className="text-red-600" size={20} />
                  <h3 className="mt-3 font-heading text-xl font-semibold uppercase text-white">{o.title}</h3>
                  {o.subtitle && <p className="mt-2 text-zinc-400 text-sm">{o.subtitle}</p>}
                  {o.link && (
                    <a href={o.link} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold uppercase text-red-500">
                      Know more <ArrowUpRight size={15} />
                    </a>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
